import clsx from "clsx";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { FCC } from "@/types";
import {
  formatDisallowedPasswordCharactersMessage,
  getDisallowedPasswordCharacters,
} from "@/utils/passwordValidation";

export interface ValidationToken {
  displayMessage: string;
  regex: RegExp;
}

interface Requirement extends ValidationToken {
  isFullfilled: boolean;
}

interface PasswordStrengthProps {
  customStyle?: string;
  /** Set to false when rendering on a light background (defaults to the
   * original dark-background/white-text styling). */
  inverted?: boolean;
  password: string;
  validationTokens: ValidationToken[];
}

export const PasswordStrength: FCC<PasswordStrengthProps> = ({
  password,
  validationTokens,
  customStyle = "",
  inverted = true,
}) => {
  const { t } = useTranslation();
  const [requirements, setRequirements] = useState<Requirement[]>(
    validationTokens.map((token) => ({
      ...token,
      isFullfilled: false,
    })),
  );

  const disallowedCharacters = useMemo(
    () => getDisallowedPasswordCharacters(password),
    [password],
  );

  const disallowedMessage =
    disallowedCharacters.length > 0
      ? formatDisallowedPasswordCharactersMessage(disallowedCharacters, t)
      : null;

  useEffect(() => {
    const updatedRequirements = validationTokens.map((token) => ({
      ...token,
      isFullfilled: token.regex.test(password),
    }));
    setRequirements(updatedRequirements);
  }, [password, validationTokens]);

  return (
    <ul className={clsx("mt-0 list-disc space-y-1 pl-5", customStyle)}>
      {requirements.map((requirement, index) => (
        <li
          key={index}
          className={clsx(
            "text-sm leading-md transition-colors duration-300",
            requirement.isFullfilled
              ? "text-foreground-success"
              : inverted
                ? "text-white"
                : "text-foreground-primary",
          )}
        >
          {requirement.displayMessage}
        </li>
      ))}
      {disallowedMessage && (
        <li
          className={clsx(
            "text-sm leading-md transition-colors duration-300",
            "text-foreground-error",
          )}
          data-testid="password-disallowed-characters"
        >
          {disallowedMessage}
        </li>
      )}
    </ul>
  );
};
